import React, { useState, useEffect, useCallback, useMemo } from "react";
import api from "../../services/api";
import { STUDY_CONSENT, STUDY_DEBRIEFING } from "../../data/study_boilerplate";
import { composeSurvey, ALL_SCALES, ALL_TOPICS, SCALE_CATEGORIES, SURVEY_PRESETS, getAllScaleIds, getPresetScaleIds, getPresetExcludedQuestionIds, countQuestions, countItems, countPageItems } from "../../data/survey_compose";
import {
  FaPlus,
  FaTrash,
  FaDownload,
  FaEdit,
  FaPlay,
  FaPause,
  FaArchive,
  FaChevronDown,
  FaChevronUp,
  FaChevronLeft,
  FaChevronRight,
  FaSpinner,
  FaCheck,
  FaFlask,
  FaTimes,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaCopy,
  FaChartBar,
} from "react-icons/fa";

const getAdminToken = () => localStorage.getItem("admin_token");
const adminHeaders = () => ({
  headers: { Authorization: `Bearer ${getAdminToken()}` },
});

// Auto-discover topic templates (same pattern as ConditionEditor)
const templateContext = require.context("../../data", false, /^\.\/templates_.*\.json$/);
const TOPIC_TEMPLATES = {};
templateContext.keys().forEach((filename) => {
  const data = templateContext(filename);
  const key = filename.replace(/^\.\/templates_/, "").replace(/\.json$/, "");
  TOPIC_TEMPLATES[key] = {
    label: data.sharedConfig?.topic?.name || key,
    data,
  };
});

const StatusBadge = ({ status }) => {
  const colors = {
    draft: "bg-gray-600 text-gray-200",
    active: "bg-green-600 text-white",
    paused: "bg-orange-600 text-white",
    completed: "bg-blue-600 text-white",
    archived: "bg-yellow-600 text-white",
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[status] || "bg-gray-600 text-gray-200"}`}>
      {status}
    </span>
  );
};

const CopyButton = ({ text }) => {
  const [copied, setCopied] = React.useState(false);
  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center ml-1.5 text-gray-500 hover:text-gray-300 transition-colors"
      title="Copy to clipboard"
    >
      {copied ? <FaCheck className="w-3 h-3 text-green-400" /> : <FaCopy className="w-3 h-3" />}
    </button>
  );
};

const inputClass = "w-full p-2 bg-gray-600 text-white rounded border border-gray-500 focus:border-blue-500 focus:outline-none";
const labelClass = "block text-sm text-gray-300 mb-1";

// ============================================
// Study Creation Wizard
// ============================================
const WIZARD_STEPS = [
  { id: "basic", label: "Basic Info" },
  { id: "conditions", label: "Topic & Conditions" },
  { id: "surveys", label: "Surveys" },
  { id: "consent", label: "Consent & Debriefing" },
  { id: "review", label: "Review & Create" },
];

const StudyCreationWizard = ({ onComplete, onCancel }) => {
  const wizardRef = React.useRef(null);
  const [step, setStepRaw] = useState(0);
  const setStep = (newStep) => {
    setStepRaw(newStep);
    wizardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const [creating, setCreating] = useState(false);
  const [creatingConditions, setCreatingConditions] = useState(false);
  const [conditionResults, setConditionResults] = useState(null);
  const [error, setError] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    id: "",
    name: "",
    description: "",
    completionCode: "",
    redirectUrl: "",
    panelName: "Tandemz",
    minDurationMinutes: 10,
    maxDurationMinutes: 30,
    stimulusPerceptionEnabled: false,
    // Topic & conditions
    selectedTopic: "",
    // Surveys
    surveyTemplate: "",
    preSurvey: { pages: [] },
    postSurvey: { pages: [] },
    customSurveyJson: "",
    surveyMode: "modular",
    surveyPreset: "streamlined",
    enabledScaleIds: getPresetScaleIds("streamlined"),
    excludedQuestionIds: getPresetExcludedQuestionIds("streamlined"),
    // Consent & debriefing
    consentTitle: "Study Information & Consent",
    consentContent: "",
    requireAcknowledgment: true,
    debriefingTitle: "Study Debriefing",
    debriefingContent: "",
  });

  const update = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));

  const currentTopicData = formData.selectedTopic ? TOPIC_TEMPLATES[formData.selectedTopic]?.data : null;

  // Build list of full study templates from the modular topic registry
  const STUDY_TEMPLATES = Object.entries(TOPIC_TEMPLATES).map(([key, topicTmpl]) => ({
    key,
    label: topicTmpl.label,
    topicData: topicTmpl.data,
  }));

  // Load a full study template: pre-fills topic, surveys, consent, debriefing, and name
  const loadFullTemplate = (templateKey) => {
    const topicTmpl = TOPIC_TEMPLATES[templateKey];
    if (!topicTmpl) return;

    setFormData((prev) => ({
      ...prev,
      name: prev.name || topicTmpl.label + " Study",
      description: prev.description || topicTmpl.data.sharedConfig?.topic?.description || "",
      selectedTopic: templateKey,
      surveyTemplate: templateKey,
      surveyMode: "modular",
      surveyPreset: "recommended",
      enabledScaleIds: getPresetScaleIds("recommended"),
      excludedQuestionIds: getPresetExcludedQuestionIds("recommended"),
      consentTitle: STUDY_CONSENT.title,
      consentContent: STUDY_CONSENT.content,
      debriefingTitle: STUDY_DEBRIEFING.title,
      debriefingContent: STUDY_DEBRIEFING.content,
    }));
  };

  // Validate current step
  const canProceed = () => {
    switch (WIZARD_STEPS[step].id) {
      case "basic":
        return formData.id.trim() && formData.name.trim();
      case "conditions":
        return !!formData.selectedTopic;
      case "surveys":
        return formData.preSurvey.pages.length > 0 || formData.surveyMode === "custom" || formData.surveyMode === "modular";
      case "consent":
        return true;
      case "review":
        return true;
      default:
        return true;
    }
  };

  // Create conditions from template
  const handleCreateConditions = async () => {
    if (!currentTopicData || !formData.id) return;
    setCreatingConditions(true);
    setError(null);
    try {
      // First create the study if not yet created
      try {
        await api.post(
          "/admin/studies",
          {
            id: formData.id,
            name: formData.name,
            description: formData.description,
            prolificConfig: {
              completionCode: formData.completionCode,
              redirectUrl: formData.redirectUrl,
            },
            sessionConfig: {
              minDurationMinutes: Number(formData.minDurationMinutes),
              maxDurationMinutes: Number(formData.maxDurationMinutes),
            },
          },
          adminHeaders()
        );
      } catch (err) {
        // 409 means study already exists — that's fine
        if (err.response?.status !== 409) throw err;
      }

      const { data } = await api.post(
        `/admin/studies/${formData.id}/create-conditions`,
        { topicTemplate: currentTopicData },
        adminHeaders()
      );
      setConditionResults(data.conditions);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create conditions");
    } finally {
      setCreatingConditions(false);
    }
  };

  // Final create/update
  const handleFinish = async () => {
    setCreating(true);
    setError(null);
    try {
      // Parse custom survey JSON if applicable
      let preSurvey = formData.preSurvey;
      let postSurvey = formData.postSurvey;
      if (formData.surveyMode === "custom" && formData.customSurveyJson.trim()) {
        try {
          const parsed = JSON.parse(formData.customSurveyJson);
          preSurvey = parsed.preSurvey || { pages: [] };
          postSurvey = parsed.postSurvey || { pages: [] };
        } catch {
          setError("Invalid JSON in custom survey configuration");
          setCreating(false);
          return;
        }
      }

      // Ensure study exists
      try {
        await api.post(
          "/admin/studies",
          {
            id: formData.id,
            name: formData.name,
            description: formData.description,
            prolificConfig: {
              completionCode: formData.completionCode,
              redirectUrl: formData.redirectUrl,
            },
            sessionConfig: {
              minDurationMinutes: Number(formData.minDurationMinutes),
              maxDurationMinutes: Number(formData.maxDurationMinutes),
            },
          },
          adminHeaders()
        );
      } catch (err) {
        if (err.response?.status !== 409) throw err;
      }

      // Update with surveys and consent
      await api.put(
        `/admin/studies/${formData.id}`,
        {
          name: formData.name,
          description: formData.description,
          preSurvey,
          postSurvey,
          consent: {
            title: formData.consentTitle,
            content: formData.consentContent,
            requireAcknowledgment: formData.requireAcknowledgment,
          },
          debriefing: {
            title: formData.debriefingTitle,
            content: formData.debriefingContent,
          },
          prolificConfig: {
            completionCode: formData.completionCode,
            redirectUrl: formData.redirectUrl,
            panelName: formData.panelName,
          },
          sessionConfig: {
            minDurationMinutes: Number(formData.minDurationMinutes),
            maxDurationMinutes: Number(formData.maxDurationMinutes),
            stimulusPerceptionEnabled: formData.stimulusPerceptionEnabled,
          },
        },
        adminHeaders()
      );

      onComplete();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create study");
    } finally {
      setCreating(false);
    }
  };

  // Step renderers
  const renderBasicInfo = () => (
    <div>
      {/* Load from template */}
      {STUDY_TEMPLATES.length > 0 && (
        <div className="bg-gray-600 rounded-lg p-4 mb-5">
          <label className={labelClass}>Load from Study Template</label>
          <p className="text-xs text-gray-400 mb-2">
            Pre-fills topic, conditions, surveys, consent, and debriefing from a template.
          </p>
          <div className="flex flex-wrap gap-2">
            {STUDY_TEMPLATES.map((tmpl) => (
              <button
                key={tmpl.key}
                type="button"
                onClick={() => loadFullTemplate(tmpl.key)}
                className={`px-3 py-2 rounded text-sm transition-colors flex items-center gap-2 ${
                  formData.selectedTopic === tmpl.key && formData.surveyTemplate === tmpl.key
                    ? "bg-blue-600 text-white"
                    : "bg-gray-500 text-gray-200 hover:bg-gray-400"
                }`}
              >
                <FaFlask className="w-3 h-3" />
                {tmpl.label}
              </button>
            ))}
          </div>
          {formData.selectedTopic && formData.surveyTemplate && (
            <p className="text-xs text-green-400 mt-2 flex items-center gap-1">
              <FaCheck className="w-2.5 h-2.5" />
              Template loaded — topic, surveys, consent, and debriefing are pre-filled. Adjust fields below as needed.
            </p>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
      <div>
        <label className={labelClass}>Study ID *</label>
        <input
          type="text"
          value={formData.id}
          onChange={(e) => update("id", e.target.value)}
          placeholder="e.g., ai_exam_2026_02"
          className={inputClass}
        />
      </div>
      <div>
        <label className={labelClass}>Name *</label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => update("name", e.target.value)}
          placeholder="AI Exam Monitoring Study"
          className={inputClass}
        />
      </div>
      <div className="col-span-2">
        <label className={labelClass}>Description</label>
        <textarea
          value={formData.description}
          onChange={(e) => update("description", e.target.value)}
          rows={2}
          className={inputClass}
        />
      </div>
      <div>
        <label className={labelClass}>Panel Name</label>
        <input
          type="text"
          value={formData.panelName}
          onChange={(e) => update("panelName", e.target.value)}
          placeholder="Tandemz"
          className={inputClass}
        />
        <p className="text-xs text-gray-500 mt-1">Used in consent and debriefing text (replaces {"{{panel}}"} placeholder)</p>
      </div>
      <div>
        <label className={labelClass}>Completion Code</label>
        <input
          type="text"
          value={formData.completionCode}
          onChange={(e) => update("completionCode", e.target.value)}
          placeholder="CNT1P9FA"
          className={inputClass}
        />
      </div>
      <div>
        <label className={labelClass}>Panel Redirect URL</label>
        <input
          type="text"
          value={formData.redirectUrl}
          onChange={(e) => update("redirectUrl", e.target.value)}
          className={inputClass}
        />
      </div>
      <div>
        <label className={labelClass}>Min Duration (minutes)</label>
        <input
          type="number"
          value={formData.minDurationMinutes}
          onChange={(e) => update("minDurationMinutes", e.target.value)}
          className={inputClass}
        />
      </div>
      <div>
        <label className={labelClass}>Max Duration (minutes)</label>
        <input
          type="number"
          value={formData.maxDurationMinutes}
          onChange={(e) => update("maxDurationMinutes", e.target.value)}
          className={inputClass}
        />
      </div>
      </div>
    </div>
  );

  const renderConditions = () => (
    <div>
      <div className="mb-4">
        <label className={labelClass}>Topic Template *</label>
        <select
          value={formData.selectedTopic}
          onChange={(e) => {
            update("selectedTopic", e.target.value);
            setConditionResults(null);
          }}
          className={inputClass}
        >
          <option value="">Select a topic...</option>
          {Object.entries(TOPIC_TEMPLATES).map(([key, tmpl]) => (
            <option key={key} value={key}>
              {tmpl.label}
            </option>
          ))}
        </select>
      </div>

      {currentTopicData && (
        <>
          <div className="bg-gray-600 rounded p-3 mb-4">
            <p className="text-sm text-gray-300 mb-2">
              <strong>Topic:</strong> {currentTopicData.sharedConfig.topic.name}
            </p>
            <p className="text-xs text-gray-400 mb-3">
              {currentTopicData.sharedConfig.topic.description}
            </p>
            <p className="text-sm text-gray-300 mb-2">
              <strong>{currentTopicData.templates.length} conditions</strong> will be created:
            </p>
            <div className="grid grid-cols-3 gap-2">
              {currentTopicData.templates.map((t) => {
                // IDs are namespaced as studyId__templateId
                const namespacedId = formData.id + "__" + t.id;
                const result = conditionResults?.find((r) => r.id === namespacedId || r.id === t.id);
                return (
                <div key={t.id} className="bg-gray-700 rounded p-2 text-xs">
                  <div className="text-white font-medium">{t.name}</div>
                  <div className="text-gray-400">{namespacedId}</div>
                  {result && (
                    <div className="mt-1">
                      {result.status === "created" ? (
                        <span className="text-green-400 flex items-center gap-1">
                          <FaCheck className="w-2 h-2" /> Created
                        </span>
                      ) : result.status === "already_exists" ? (
                        <span className="text-yellow-400">Already exists</span>
                      ) : null}
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          </div>

          {!conditionResults && (
            <button
              onClick={handleCreateConditions}
              disabled={creatingConditions || !formData.id.trim()}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white rounded transition-colors flex items-center gap-2"
            >
              {creatingConditions ? (
                <FaSpinner className="animate-spin" />
              ) : (
                <FaPlus />
              )}
              Create Conditions
            </button>
          )}
          {!formData.id.trim() && (
            <p className="text-xs text-yellow-400 mt-2">Enter a Study ID in Step 1 first</p>
          )}
          {conditionResults && (
            <div className="flex items-center gap-2 text-green-400 text-sm mt-2">
              <FaCheck /> All conditions created and linked to study
            </div>
          )}
        </>
      )}
    </div>
  );

  // Reactive composition: recompose survey whenever scales or topic change
  const composedSurvey = useMemo(() => {
    if (formData.surveyMode !== "modular") return null;
    const topic = formData.selectedTopic ? ALL_TOPICS[formData.selectedTopic] : null;
    return composeSurvey({ enabledScaleIds: formData.enabledScaleIds, excludedQuestionIds: formData.excludedQuestionIds, topic });
  }, [formData.surveyMode, formData.enabledScaleIds, formData.excludedQuestionIds, formData.selectedTopic]);

  // Sync composed survey into formData when it changes
  useEffect(() => {
    if (composedSurvey) {
      setFormData((prev) => ({
        ...prev,
        preSurvey: composedSurvey.preSurvey,
        postSurvey: composedSurvey.postSurvey,
        consentTitle: composedSurvey.consent.title || prev.consentTitle,
        consentContent: composedSurvey.consent.content || prev.consentContent,
        requireAcknowledgment: composedSurvey.consent.requireAcknowledgment ?? prev.requireAcknowledgment,
        debriefingTitle: composedSurvey.debriefing.title || prev.debriefingTitle,
        debriefingContent: composedSurvey.debriefing.content || prev.debriefingContent,
      }));
    }
  }, [composedSurvey]);

  const toggleScale = (scaleId) => {
    const scale = ALL_SCALES[scaleId];
    if (scale?.required) return; // Can't toggle required scales
    setFormData((prev) => {
      const ids = prev.enabledScaleIds.includes(scaleId)
        ? prev.enabledScaleIds.filter((id) => id !== scaleId)
        : [...prev.enabledScaleIds, scaleId];
      return { ...prev, enabledScaleIds: ids };
    });
  };

  const [showPreviewJson, setShowPreviewJson] = useState(false);
  const [expandedProvenance, setExpandedProvenance] = useState(null);
  const [expandedScales, setExpandedScales] = useState(new Set());

  const toggleExpandScale = (scaleId) => {
    setExpandedScales((prev) => {
      const next = new Set(prev);
      if (next.has(scaleId)) next.delete(scaleId);
      else next.add(scaleId);
      return next;
    });
  };

  const toggleQuestion = (qualifiedId) => {
    setFormData((prev) => {
      const ids = prev.excludedQuestionIds.includes(qualifiedId)
        ? prev.excludedQuestionIds.filter((id) => id !== qualifiedId)
        : [...prev.excludedQuestionIds, qualifiedId];
      return { ...prev, excludedQuestionIds: ids };
    });
  };

  const applyPreset = (presetId) => {
    setFormData((prev) => ({
      ...prev,
      surveyPreset: presetId,
      enabledScaleIds: getPresetScaleIds(presetId),
      excludedQuestionIds: getPresetExcludedQuestionIds(presetId),
    }));
    setExpandedScales(new Set());
  };

  // Track whether current selection still matches a preset
  const activePreset = useMemo(() => {
    for (const [presetId, preset] of Object.entries(SURVEY_PRESETS)) {
      const expectedScales = getPresetScaleIds(presetId);
      const expectedExcluded = getPresetExcludedQuestionIds(presetId);
      const scalesMatch = expectedScales.length === formData.enabledScaleIds.length &&
        expectedScales.every((id) => formData.enabledScaleIds.includes(id));
      const excludedMatch = expectedExcluded.length === formData.excludedQuestionIds.length &&
        expectedExcluded.every((id) => formData.excludedQuestionIds.includes(id));
      if (scalesMatch && excludedMatch) return presetId;
    }
    return null;
  }, [formData.enabledScaleIds, formData.excludedQuestionIds]);

  const ORIGIN_BADGES = {
    established: { label: "Established", color: "bg-green-700 text-green-200" },
    adapted: { label: "Adapted", color: "bg-blue-700 text-blue-200" },
    custom: { label: "Custom", color: "bg-gray-500 text-gray-200" },
    mixed: { label: "Mixed", color: "bg-purple-700 text-purple-200" },
  };

  // Render the question detail for a single question (used in expanded view)
  const renderQuestionDetail = (q, qualifiedId, enabled) => {
    const isExcluded = formData.excludedQuestionIds.includes(qualifiedId);
    const itemCount = q.type === "likert_battery" && q.items ? q.items.length : 1;

    return (
      <div key={q.questionId} className={`p-2 rounded ${isExcluded ? "opacity-50" : ""}`}>
        <div className="flex items-start gap-2">
          <input
            type="checkbox"
            checked={!isExcluded && enabled}
            onChange={() => toggleQuestion(qualifiedId)}
            disabled={!enabled}
            className="rounded mt-0.5"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-xs font-mono text-gray-500">{q.questionId}</span>
              <span className="text-[10px] text-gray-600 bg-gray-800 px-1 rounded">{q.type}</span>
              {itemCount > 1 && (
                <span className="text-[10px] text-gray-500">{itemCount} items</span>
              )}
            </div>
            {q.label && <p className="text-xs text-gray-300 mt-0.5">{q.label}</p>}
            {q.type === "likert_battery" && q.items && (
              <ul className="mt-1 space-y-0.5">
                {q.items.map((item) => (
                  <li key={item.itemId} className="text-xs text-gray-300 pl-3 flex gap-1">
                    <span className="text-gray-600 shrink-0">&bull;</span>
                    <span>{item.text}</span>
                    {item.reverse && <span className="text-yellow-600 text-[10px]">(R)</span>}
                  </li>
                ))}
              </ul>
            )}
            {q.source && <p className="text-[10px] text-gray-600 mt-1 italic">{q.source}</p>}
          </div>
        </div>
      </div>
    );
  };

  const renderModularSurveys = () => {
    const topic = formData.selectedTopic ? ALL_TOPICS[formData.selectedTopic] : null;
    const totalPages = (composedSurvey?.preSurvey?.pages?.length || 0) + (composedSurvey?.postSurvey?.pages?.length || 0);
    const totalItems = composedSurvey
      ? countPageItems(composedSurvey.preSurvey.pages) + countPageItems(composedSurvey.postSurvey.pages)
      : 0;

    return (
      <div>
        {/* Topic info */}
        {topic ? (
          <div className="bg-gray-600 rounded p-3 mb-4">
            <p className="text-sm text-white font-medium mb-1">Topic: {topic.label}</p>
            <p className="text-xs text-gray-400 mb-2">
              Includes {Object.keys(topic.pages).length} topic-specific pages:
            </p>
            <div className="space-y-1">
              {Object.entries(topic.pages).map(([pageKey, pageEntry]) => {
                const tProv = topic.provenance?.[pageKey];
                const tBadge = tProv ? ORIGIN_BADGES[tProv.origin] : null;
                const iCount = countItems(pageEntry.page.questions);
                const topicScaleExpanded = expandedScales.has(`topic.${pageKey}`);
                return (
                  <div key={pageKey}>
                    <div
                      onClick={() => toggleExpandScale(`topic.${pageKey}`)}
                      className="flex items-center gap-2 text-xs cursor-pointer hover:bg-gray-500/30 rounded p-1 -m-1"
                    >
                      <span className="text-gray-400">
                        {topicScaleExpanded ? <FaChevronDown className="w-2.5 h-2.5" /> : <FaChevronRight className="w-2.5 h-2.5" />}
                      </span>
                      <span className="text-gray-300">{pageEntry.page.title || pageKey}</span>
                      <span className="text-gray-500">({iCount} items, {pageEntry.phase})</span>
                      {tBadge && (
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${tBadge.color}`}>
                          {tBadge.label}
                        </span>
                      )}
                      {tProv && (tProv.instruments?.length > 0 || tProv.notes) && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedProvenance(expandedProvenance === `topic_${pageKey}` ? null : `topic_${pageKey}`);
                          }}
                          className="text-gray-500 hover:text-gray-300 text-[10px] underline"
                        >
                          {expandedProvenance === `topic_${pageKey}` ? "hide" : "sources"}
                        </button>
                      )}
                    </div>
                    {expandedProvenance === `topic_${pageKey}` && tProv && (
                      <div className="ml-6 mt-1 mb-1 p-2 bg-gray-800 rounded text-xs space-y-1">
                        {tProv.instruments?.length > 0 && tProv.instruments.map((inst, i) => (
                          <div key={i} className="text-gray-300">
                            <span className="text-white font-medium">{inst.name}</span>
                            <span className="text-gray-500 ml-1">({inst.citation})</span>
                          </div>
                        ))}
                        {tProv.notes && <p className="text-gray-400 italic">{tProv.notes}</p>}
                      </div>
                    )}
                    {topicScaleExpanded && (
                      <div className="ml-6 mt-1 mb-1 space-y-1 border-l border-gray-700 pl-2">
                        {pageEntry.page.questions?.map((q) =>
                          renderQuestionDetail(q, `topic.${pageKey}.${q.questionId}`, true)
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="bg-yellow-900/30 border border-yellow-700 rounded p-3 mb-4">
            <p className="text-sm text-yellow-300">Select a topic in Step 2 to include topic-specific pages.</p>
          </div>
        )}

        {/* Preset selector */}
        <div className="flex items-center gap-2 mb-4">
          <span className="text-xs text-gray-400">Preset:</span>
          {Object.entries(SURVEY_PRESETS).map(([presetId, preset]) => (
            <button
              key={presetId}
              type="button"
              onClick={() => applyPreset(presetId)}
              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                activePreset === presetId
                  ? "bg-blue-600 text-white"
                  : "bg-gray-700 text-gray-300 hover:bg-gray-600"
              }`}
              title={preset.description}
            >
              {preset.label}
            </button>
          ))}
          {activePreset === null && (
            <span className="text-xs text-yellow-400 italic">Custom selection</span>
          )}
        </div>

        {/* Scale toggles by category */}
        <div className="space-y-4">
          {SCALE_CATEGORIES.map((cat) => (
            <div key={cat.id}>
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                {cat.phase === "pre" ? "Pre" : "Post"} &mdash; {cat.label}
              </h4>
              <div className="space-y-1">
                {cat.scaleIds.map((scaleId) => {
                  const scale = ALL_SCALES[scaleId];
                  if (!scale) return null;
                  const enabled = formData.enabledScaleIds.includes(scaleId);
                  const iCount = countItems(scale.page.questions);
                  const excludedInScale = scale.page.questions?.filter(
                    (q) => formData.excludedQuestionIds.includes(`${scaleId}.${q.questionId}`)
                  ).length || 0;
                  const prov = scale.provenance;
                  const badge = prov ? ORIGIN_BADGES[prov.origin] : null;
                  const isProvExpanded = expandedProvenance === scaleId;
                  const isQuestionsExpanded = expandedScales.has(scaleId);
                  return (
                    <div key={scaleId}>
                      <div
                        onClick={() => toggleExpandScale(scaleId)}
                        className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                          enabled ? "bg-gray-600" : "bg-gray-700/50"
                        } hover:bg-gray-600`}
                      >
                        <span className="text-gray-400">
                          {isQuestionsExpanded ? <FaChevronDown className="w-3 h-3" /> : <FaChevronRight className="w-3 h-3" />}
                        </span>
                        <input
                          type="checkbox"
                          checked={enabled}
                          onChange={() => toggleScale(scaleId)}
                          onClick={(e) => e.stopPropagation()}
                          disabled={scale.required}
                          className="rounded"
                        />
                        {scale.required && <FaLock className="w-2.5 h-2.5 text-gray-500" />}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-white">{scale.label}</span>
                            <span className="text-xs text-gray-400">
                              ({iCount} items{excludedInScale > 0 && enabled ? `, ${excludedInScale} excluded` : ""})
                            </span>
                            {badge && (
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${badge.color}`}>
                                {badge.label}
                              </span>
                            )}
                            {prov && (prov.instruments?.length > 0 || prov.notes) && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setExpandedProvenance(isProvExpanded ? null : scaleId);
                                }}
                                className="text-gray-500 hover:text-gray-300 text-[10px] underline"
                              >
                                {isProvExpanded ? "hide" : "sources"}
                              </button>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 truncate">{scale.description}</p>
                        </div>
                      </div>
                      {isProvExpanded && prov && (
                        <div className="ml-10 mt-1 mb-2 p-2 bg-gray-800 rounded text-xs space-y-1">
                          {prov.instruments?.length > 0 && prov.instruments.map((inst, i) => (
                            <div key={i} className="text-gray-300">
                              <span className="text-white font-medium">{inst.name}</span>
                              <span className="text-gray-500 ml-1">({inst.citation})</span>
                              {inst.items?.length > 0 && (
                                <span className="text-gray-500 ml-1">
                                  — items: {inst.items.join(", ")}
                                </span>
                              )}
                            </div>
                          ))}
                          {prov.notes && <p className="text-gray-400 italic">{prov.notes}</p>}
                        </div>
                      )}
                      {isQuestionsExpanded && (
                        <div className="ml-10 mt-1 mb-2 space-y-1 border-l border-gray-700 pl-2">
                          {scale.page.questions?.map((q) =>
                            renderQuestionDetail(q, `${scaleId}.${q.questionId}`, enabled)
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Stimulus Perception Check toggle */}
        <div className="mt-4 pt-3 border-t border-gray-600">
          <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
            Post &mdash; Stimulus Perception
          </h4>
          <div
            className={`flex items-center gap-3 p-2 rounded transition-colors ${
              formData.stimulusPerceptionEnabled ? "bg-gray-600" : "bg-gray-700/50"
            }`}
          >
            <input
              type="checkbox"
              checked={formData.stimulusPerceptionEnabled}
              onChange={() => update("stimulusPerceptionEnabled", !formData.stimulusPerceptionEnabled)}
              className="rounded"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm text-white">Stimulus Perception Check</span>
                <span className="text-xs text-gray-400">
                  (condition-specific, ~19 items)
                </span>
                <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-500/20 text-blue-300">
                  Dynamic
                </span>
              </div>
              <p className="text-xs text-gray-500">
                Shows condition-specific exemplar posts and post+comment pairs. Participants rate them on emotionality, group targeting, authenticity, and engagement quality.
              </p>
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="mt-4 pt-3 border-t border-gray-600 flex items-center justify-between">
          <div className="text-sm text-gray-300">
            <span className="font-medium text-white">{totalPages}</span> pages,{" "}
            <span className="font-medium text-white">{totalItems}</span> items
            <span className="text-gray-500 ml-2">
              (pre: {composedSurvey?.preSurvey?.pages?.length || 0}, post: {composedSurvey?.postSurvey?.pages?.length || 0})
            </span>
            {formData.excludedQuestionIds.length > 0 && (
              <span className="text-yellow-400 ml-2">
                ({formData.excludedQuestionIds.length} question{formData.excludedQuestionIds.length !== 1 ? "s" : ""} excluded)
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={() => setShowPreviewJson(!showPreviewJson)}
            className="text-xs text-gray-400 hover:text-gray-200 flex items-center gap-1"
          >
            {showPreviewJson ? <FaEyeSlash className="w-3 h-3" /> : <FaEye className="w-3 h-3" />}
            {showPreviewJson ? "Hide" : "Preview"} JSON
          </button>
        </div>

        {showPreviewJson && composedSurvey && (
          <pre className="mt-2 p-3 bg-gray-900 rounded text-xs text-gray-300 overflow-auto max-h-64 font-mono">
            {JSON.stringify({ preSurvey: composedSurvey.preSurvey, postSurvey: composedSurvey.postSurvey }, null, 2)}
          </pre>
        )}
      </div>
    );
  };

  const renderSurveys = () => (
    <div>
      <div className="flex gap-2 mb-4">
        {[
          { mode: "modular", label: "Modular" },
          { mode: "custom", label: "Custom JSON" },
        ].map(({ mode, label }) => (
          <button
            key={mode}
            onClick={() => update("surveyMode", mode)}
            className={`px-3 py-1.5 rounded text-sm ${
              formData.surveyMode === mode
                ? "bg-blue-600 text-white"
                : "bg-gray-600 text-gray-300 hover:bg-gray-500"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {formData.surveyMode === "modular" && renderModularSurveys()}
      {formData.surveyMode === "custom" && (
        <div>
          <label className={labelClass}>
            Custom Survey JSON (must contain &quot;preSurvey&quot; and &quot;postSurvey&quot; keys)
          </label>
          <textarea
            value={formData.customSurveyJson}
            onChange={(e) => update("customSurveyJson", e.target.value)}
            rows={12}
            placeholder='{"preSurvey": {"pages": [...]}, "postSurvey": {"pages": [...]}}'
            className={inputClass + " font-mono text-xs"}
          />
        </div>
      )}
    </div>
  );

  const renderConsent = () => (
    <div className="space-y-6">
      <div>
        <h4 className="text-sm font-semibold text-white mb-3">Consent</h4>
        <div className="space-y-3">
          <div>
            <label className={labelClass}>Title</label>
            <input
              type="text"
              value={formData.consentTitle}
              onChange={(e) => update("consentTitle", e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Content (supports markdown)</label>
            <textarea
              value={formData.consentContent}
              onChange={(e) => update("consentContent", e.target.value)}
              rows={8}
              className={inputClass}
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-300">
            <input
              type="checkbox"
              checked={formData.requireAcknowledgment}
              onChange={(e) => update("requireAcknowledgment", e.target.checked)}
              className="rounded"
            />
            Require acknowledgment checkbox
          </label>
        </div>
      </div>

      <div>
        <h4 className="text-sm font-semibold text-white mb-3">Debriefing</h4>
        <div className="space-y-3">
          <div>
            <label className={labelClass}>Title</label>
            <input
              type="text"
              value={formData.debriefingTitle}
              onChange={(e) => update("debriefingTitle", e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Content (supports markdown)</label>
            <textarea
              value={formData.debriefingContent}
              onChange={(e) => update("debriefingContent", e.target.value)}
              rows={8}
              className={inputClass}
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderReview = () => (
    <div className="space-y-4">
      <div className="bg-gray-600 rounded p-3">
        <h4 className="text-sm font-semibold text-white mb-2">Basic Info</h4>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div><span className="text-gray-400">ID:</span> <span className="text-white">{formData.id}</span></div>
          <div><span className="text-gray-400">Name:</span> <span className="text-white">{formData.name}</span></div>
          <div><span className="text-gray-400">Duration:</span> <span className="text-white">{formData.minDurationMinutes}-{formData.maxDurationMinutes}m</span></div>
          <div><span className="text-gray-400">Code:</span> <span className="text-white">{formData.completionCode || "-"}</span></div>
        </div>
      </div>

      <div className="bg-gray-600 rounded p-3">
        <h4 className="text-sm font-semibold text-white mb-2">Topic & Conditions</h4>
        <p className="text-sm text-gray-300">
          {TOPIC_TEMPLATES[formData.selectedTopic]?.label || "None selected"}
        </p>
        {conditionResults ? (
          <p className="text-xs text-green-400 mt-1">
            {conditionResults.length} conditions created
          </p>
        ) : (
          <p className="text-xs text-yellow-400 mt-1">Conditions not yet created</p>
        )}
      </div>

      <div className="bg-gray-600 rounded p-3">
        <h4 className="text-sm font-semibold text-white mb-2">Surveys</h4>
        <div className="text-sm text-gray-300">
          <div>Pre-survey: {formData.preSurvey.pages.length} pages</div>
          <div>Post-survey: {formData.postSurvey.pages.length} pages</div>
        </div>
      </div>

      <div className="bg-gray-600 rounded p-3">
        <h4 className="text-sm font-semibold text-white mb-2">Consent & Debriefing</h4>
        <div className="text-sm text-gray-300">
          <div>Consent: {formData.consentContent ? "Configured" : "Not set"}</div>
          <div>Debriefing: {formData.debriefingContent ? "Configured" : "Not set"}</div>
        </div>
      </div>

      <div className="bg-gray-600 rounded p-3">
        <p className="text-sm text-gray-300 flex items-center">
          <span className="text-gray-400">Participant link: </span>
          <code className="bg-gray-800 px-2 py-0.5 rounded text-blue-300 text-xs">
            {window.location.origin}/study?s={formData.id}&pid=PROLIFIC_PID
          </code>
          <CopyButton text={`${window.location.origin}/study?s=${formData.id}&pid=PROLIFIC_PID`} />
        </p>
      </div>
    </div>
  );

  const stepRenderers = [renderBasicInfo, renderConditions, renderSurveys, renderConsent, renderReview];

  return (
    <div ref={wizardRef} className="bg-gray-700 rounded-lg p-6 mb-6">
      {/* Step indicator */}
      <div className="flex items-center mb-6">
        {WIZARD_STEPS.map((s, i) => (
          <React.Fragment key={s.id}>
            <button
              onClick={() => i < step && setStep(i)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                i === step
                  ? "bg-blue-600 text-white"
                  : i < step
                  ? "bg-green-700 text-green-200 cursor-pointer hover:bg-green-600"
                  : "bg-gray-600 text-gray-400"
              }`}
            >
              <span className="w-5 h-5 rounded-full border flex items-center justify-center text-xs">
                {i < step ? <FaCheck className="w-2.5 h-2.5" /> : i + 1}
              </span>
              {s.label}
            </button>
            {i < WIZARD_STEPS.length - 1 && (
              <div className={`flex-1 h-px mx-2 ${i < step ? "bg-green-600" : "bg-gray-600"}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Error display */}
      {error && (
        <div className="bg-red-900/30 border border-red-700 text-red-300 p-3 rounded mb-4 flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-200">
            <FaTimes />
          </button>
        </div>
      )}

      {/* Step content */}
      <div className="mb-6">{stepRenderers[step]()}</div>

      {/* Navigation */}
      <div className="flex justify-between">
        <div>
          {step > 0 && (
            <button
              onClick={() => setStep(step - 1)}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded transition-colors flex items-center gap-2"
            >
              <FaChevronLeft className="w-3 h-3" /> Back
            </button>
          )}
        </div>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded transition-colors"
          >
            Cancel
          </button>
          {step < WIZARD_STEPS.length - 1 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:text-gray-400 text-white rounded transition-colors flex items-center gap-2"
            >
              Next <FaChevronRight className="w-3 h-3" />
            </button>
          ) : (
            <button
              onClick={handleFinish}
              disabled={creating}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded transition-colors flex items-center gap-2"
            >
              {creating ? <FaSpinner className="animate-spin" /> : <FaCheck />}
              Create Study
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================
// Main StudyManager Component
// ============================================
const StudyManager = () => {
  const [studies, setStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedStudy, setExpandedStudy] = useState(null);
  const [showCreateWizard, setShowCreateWizard] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [activationConflict, setActivationConflict] = useState(null); // { studyId, conflictingStudy }

  const fetchStudies = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/studies", adminHeaders());
      setStudies(data);
      setError(null);
    } catch (err) {
      setError("Failed to fetch studies");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStudies();
  }, [fetchStudies]);

  const handleStatusChange = async (studyId, newStatus, { force = false } = {}) => {
    try {
      await api.patch(
        `/admin/studies/${studyId}/status`,
        { status: newStatus, force },
        adminHeaders()
      );
      setActivationConflict(null);
      fetchStudies();
    } catch (err) {
      if (err.response?.status === 409 && err.response.data?.conflictingStudy) {
        setActivationConflict({
          studyId,
          conflictingStudy: err.response.data.conflictingStudy,
        });
        return;
      }
      setError(err.response?.data?.message || "Failed to update status");
    }
  };

  const handleDelete = async (studyId) => {
    if (!window.confirm(
      `Are you sure you want to delete study "${studyId}"?\n\n` +
      `This will CASCADE DELETE all linked data:\n` +
      `- Study-linked conditions\n` +
      `- Instances (all participant sessions)\n` +
      `- Users, messages, comments\n` +
      `- Interactions, feed impressions\n` +
      `- Hashtags, notifications\n` +
      `- Study assignments & survey responses\n\n` +
      `This action cannot be undone.`
    )) {
      return;
    }
    try {
      await api.delete(`/admin/studies/${studyId}`, adminHeaders());
      fetchStudies();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete study");
    }
  };

  const handleExport = async (studyId) => {
    setExporting(studyId);
    try {
      const { data } = await api.get(`/admin/studies/${studyId}/export`, adminHeaders());
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `study-export-${studyId}-${new Date().toISOString().split("T")[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError("Failed to export study data");
    } finally {
      setExporting(null);
    }
  };

  const handleCleanTestData = async (studyId) => {
    if (!window.confirm(`Delete ALL test data for study "${studyId}"? This includes test assignments, instances, survey responses, and all associated content.`)) {
      return;
    }
    try {
      const { data } = await api.delete(`/admin/studies/${studyId}/test-data`, adminHeaders());
      const d = data.deleted;
      alert(`Cleaned up: ${d.assignments} assignments, ${d.instances} instances, ${d.surveyResponses} survey responses, ${d.users} users, ${d.messages} messages, ${d.comments} comments`);
      fetchStudies();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to clean up test data");
    }
  };

  const toggleExpand = async (studyId) => {
    if (expandedStudy?.id === studyId) {
      setExpandedStudy(null);
      return;
    }
    try {
      const { data } = await api.get(`/admin/studies/${studyId}`, adminHeaders());
      setExpandedStudy({ id: studyId, data });
    } catch (err) {
      setError("Failed to fetch study details");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <FaSpinner className="animate-spin text-blue-400 text-2xl" />
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div className="bg-red-900/30 border border-red-700 text-red-300 p-3 rounded mb-4 flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-200">
            &times;
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <FaFlask /> Studies
        </h2>
        {!showCreateWizard && (
          <button
            onClick={() => setShowCreateWizard(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
          >
            <FaPlus /> New Study
          </button>
        )}
      </div>

      {/* Creation Wizard */}
      {showCreateWizard && (
        <StudyCreationWizard
          onComplete={() => {
            setShowCreateWizard(false);
            fetchStudies();
          }}
          onCancel={() => setShowCreateWizard(false)}
        />
      )}

      {/* Study List */}
      {studies.length === 0 && !showCreateWizard ? (
        <div className="text-center py-12 text-gray-400">
          <FaFlask className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No studies yet. Create one to get started.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {studies.map((study) => (
            <div key={study._id} className="bg-gray-700 rounded-lg overflow-hidden">
              {/* Study Row */}
              <div
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-650"
                onClick={() => toggleExpand(study._id)}
              >
                <div className="flex items-center gap-4">
                  <button className="text-gray-400">
                    {expandedStudy?.id === study._id ? <FaChevronUp /> : <FaChevronDown />}
                  </button>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-white font-semibold">{study.name}</span>
                      <StatusBadge status={study.status} />
                    </div>
                    <span className="text-sm text-gray-400">{study._id}</span>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right text-sm text-gray-300">
                    <div>{study.assignmentCounts?.total || 0} participants</div>
                    <div className="text-gray-400">{study.assignmentCounts?.completed || 0} completed</div>
                    {study.assignmentCounts?.test > 0 && (
                      <div className="text-yellow-400">{study.assignmentCounts.test} test</div>
                    )}
                  </div>
                  <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                    {(study.status === "draft" || study.status === "paused") && (
                      <button
                        onClick={() => handleStatusChange(study._id, "active")}
                        className="p-2 bg-green-600 hover:bg-green-700 text-white rounded transition-colors"
                        title={study.status === "paused" ? "Resume" : "Activate"}
                      >
                        <FaPlay className="w-3 h-3" />
                      </button>
                    )}
                    {study.status === "active" && (
                      <button
                        onClick={() => handleStatusChange(study._id, "paused")}
                        className="p-2 bg-orange-600 hover:bg-orange-700 text-white rounded transition-colors"
                        title="Pause"
                      >
                        <FaPause className="w-3 h-3" />
                      </button>
                    )}
                    {(study.status === "active" || study.status === "paused") && (
                      <button
                        onClick={() => handleStatusChange(study._id, "completed")}
                        className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                        title="Complete"
                      >
                        <FaCheck className="w-3 h-3" />
                      </button>
                    )}
                    {["active", "paused", "completed"].includes(study.status) && (
                      <button
                        onClick={() => handleStatusChange(study._id, "archived")}
                        className="p-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded transition-colors"
                        title="Archive"
                      >
                        <FaArchive className="w-3 h-3" />
                      </button>
                    )}
                    <button
                      onClick={() => window.location.href = `/admin/studies/${study._id}/analytics`}
                      className="p-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded transition-colors"
                      title="Analytics"
                    >
                      <FaChartBar className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => handleExport(study._id)}
                      disabled={exporting === study._id}
                      className="p-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors"
                      title="Export Data"
                    >
                      {exporting === study._id ? (
                        <FaSpinner className="w-3 h-3 animate-spin" />
                      ) : (
                        <FaDownload className="w-3 h-3" />
                      )}
                    </button>
                    {study.assignmentCounts?.test > 0 && (
                      <button
                        onClick={() => handleCleanTestData(study._id)}
                        className="p-2 bg-yellow-600 hover:bg-yellow-500 text-white rounded transition-colors"
                        title={`Clean ${study.assignmentCounts.test} test entries`}
                      >
                        <FaTimes className="w-3 h-3" />
                      </button>
                    )}
                    {study.status !== "active" && study.status !== "paused" && (
                      <button
                        onClick={() => handleDelete(study._id)}
                        className="p-2 bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
                        title="Delete"
                      >
                        <FaTrash className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Detail */}
              {expandedStudy?.id === study._id && expandedStudy.data && (
                <div className="border-t border-gray-600 p-4">
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-300 mb-2">Conditions</h4>
                      {expandedStudy.data.conditions?.length > 0 ? (
                        <div className="space-y-1">
                          {expandedStudy.data.conditions.map((c) => {
                            const stats = expandedStudy.data.stats?.conditionCounts?.find(
                              (s) => s._id === c.conditionId
                            );
                            return (
                              <div key={c.conditionId} className="flex justify-between text-sm">
                                <span className="text-gray-300">{c.label || c.conditionId}</span>
                                <span className="text-gray-400">
                                  {stats?.total || 0} assigned / {stats?.completed || 0} done
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-400">No conditions linked</p>
                      )}
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-gray-300 mb-2">Surveys</h4>
                      <div className="space-y-1 text-sm text-gray-400">
                        <div>Pre-survey: {expandedStudy.data.preSurvey?.pages?.length || 0} pages</div>
                        <div>Post-survey: {expandedStudy.data.postSurvey?.pages?.length || 0} pages</div>
                      </div>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-gray-300 mb-2">Configuration</h4>
                      <div className="space-y-1 text-sm text-gray-400">
                        <div>Min duration: {expandedStudy.data.sessionConfig?.minDurationMinutes || "-"}m</div>
                        <div>Max duration: {expandedStudy.data.sessionConfig?.maxDurationMinutes || "-"}m</div>
                        <div>Completion code: {expandedStudy.data.prolificConfig?.completionCode || "-"}</div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-600">
                    <div className="text-sm text-gray-400">
                      <div className="mb-1 flex items-center">
                        <span>Participant link: </span>
                        <code className="bg-gray-800 px-2 py-0.5 rounded text-blue-300 text-xs">
                          {window.location.origin}/study?s={study._id}&pid=PROLIFIC_PID
                        </code>
                        <CopyButton text={`${window.location.origin}/study?s=${study._id}&pid=PROLIFIC_PID`} />
                      </div>
                      <div className="flex items-center">
                        <span>Test link: </span>
                        <code className="bg-gray-800 px-2 py-0.5 rounded text-yellow-300 text-xs">
                          {window.location.origin}/study?s={study._id}&pid=test_001&test=1
                        </code>
                        <CopyButton text={`${window.location.origin}/study?s=${study._id}&pid=test_001&test=1`} />
                      </div>
                    </div>
                    {study.assignmentCounts?.test > 0 && (
                      <button
                        onClick={() => handleCleanTestData(study._id)}
                        className="px-3 py-1.5 bg-yellow-600 hover:bg-yellow-500 text-white rounded text-xs font-medium transition-colors"
                      >
                        Clean Test Data ({study.assignmentCounts.test})
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {activationConflict && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 border border-gray-600 rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-white mb-2">Another study is active</h3>
            <p className="text-gray-300 text-sm mb-4">
              <span className="font-semibold text-white">{activationConflict.conflictingStudy.name}</span> is currently active.
              Activating this study will automatically pause it. Continue?
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setActivationConflict(null)}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleStatusChange(activationConflict.studyId, "active", { force: true })}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded transition-colors"
              >
                Pause other & activate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudyManager;
